<template>
  <hit-dialog
    size="small"
    :visible="visible"
    :title="t('hit-base.common.create-dialog-title')"
    color="panel"
    :message="t('hit-base.common.create-dialog-message')"
    :ok-button-label="t('hit-app.common.create')"
    ok-button-icon="check"
    ok-button-color="accent"
    cancel-button-icon="clear"
    :cancel-button-label="t('hit-components.common.cancel')"
    :show-cancel-button="true"
    @ok="onConfirmCreate"
    @cancel="onCancelCreate"
  />
</template>
<script>
import {HitDialog} from '@hit/components/src';
import {useI18n} from 'vue-i18n';
import {DataService, useNotificationsStore} from '@hit/base/src';
import {v4 as uuidv4} from 'uuid';
import {HitBannerNotificationItem} from '@hit/base/src/store/pinia/notifications';

export default {
  name: 'CreateItemDialog',
  components: {HitDialog},
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    apiRoute: {
      type: String,
      required: true,
    },
    routerRoute: {
      type: String,
      required: true,
    },
    initCols: {
      type: Object,
      required: false,
      default: () => {},
    },
    useCustomApi: {
      type: Boolean,
      required: false,
      default: false,
    },
    tab: {
      type: String,
      required: false,
      default: null,
    },
  },
  setup() {
    const {t} = useI18n();
    const notificationsStore = useNotificationsStore();
    return {t, notificationsStore};
  },
  data() {
    return {
      id: null,
    };
  },
  mounted() {
    document.addEventListener('keydown', this.onDialogKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onDialogKeydown);
  },
  methods: {
    onDialogKeydown(event) {
      if (!this.visible || event.isComposing) {
        return;
      }
      if (event.key !== 'Enter') {
        return;
      }
      event.preventDefault();
      this.onConfirmCreate();
    },
    onConfirmCreate() {
      this.$emit('hide');
      if (this.useCustomApi) {
        this.createCustomApiItem();
      } else {
        this.createItem();
      }
    },
    onCancelCreate() {
      this.$emit('hide');
    },
    isCreateWithoutStaffError(error) {
      const responseData = error?.response?.data;
      const message = String(responseData?.message ?? '').toLowerCase();
      const details = String(responseData?.details ?? '').toLowerCase();
      const code = String(responseData?.code ?? '');
      const rawError = `${message} ${details}`;

      const referencesStaffConstraint =
        rawError.includes('_user_id_staff') ||
        ((rawError.includes('creation_user_id') ||
          rawError.includes('modification_user_id')) &&
          rawError.includes('staff'));

      return code === '23503' && referencesStaffConstraint;
    },
    async createItem() {
      this.id = uuidv4();
      try {
        await DataService.create(this.apiRoute, {
          id: this.id,
          ...this.initCols,
        });
        this.$router.push({
          name: this.routerRoute,
          params: {
            id: this.id,
          },
          query: {
            newItem: true,
            ...(this.tab ? {tab: this.tab} : {}),
          },
        });
      } catch (error) {
        if (this.isCreateWithoutStaffError(error)) {
          this.notificationsStore.insertBannerNotification(
            this.t('hit-base.banners.link-to-staff'),
            HitBannerNotificationItem.WARNING,
          );
          return;
        }
        this.notificationsStore.insertBannerNotification(
          this.t('hit-base.banners.unknown-error-title'),
          HitBannerNotificationItem.DANGER,
        );
      }
    },
    async createCustomApiItem() {
      this.id = uuidv4();
      this.$router.push({
        name: this.routerRoute,
        params: {
          id: this.id,
        },
        query: {
          newItem: true,
        },
      });
    },
  },
};
</script>
